/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import CustomCountryCode from "@components/CustomContryCode/CustomContryCode";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import {
  useLazyGetAddressTypeQuery,
  useLazyGetCivilStatusQuery,
  useLazyGetMatrimonialRegimesQuery,
  useLazyGetPersonTypeQuery,
  useLazyGetStatesQuery,
} from "@api/catalogs";
import { useLazyGetAddressQuery, usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { usePatchPersonMutation, usePostPersonMutation } from "@api/personasApi";
import { ADDRESS_CODES, PERSON_CATALOG } from "@helpers/constantes";
import { resolveFieldsForPerson, hasEnabledPersonFields } from "@components/PersonForm/personHelpers";
import {
  AddressFormValues,
  buildAddressPayload,
  buildInitialValues,
  buildPersonPatchPayload,
  buildValidationSchema,
  OTHER_COLONIA_ID,
  OTHER_PERSON_ID,
  PersonFormValues,
} from "@components/PersonForm/personFormSchema";
import { getPersonFullName } from "@helpers/funciones";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { AddressData, ApiError, PersonaStructure, PersonFormsConfig } from "@interfaces/response";
import { Address, CivilStatus, MatrimonialRegimesData, StateStructure } from "@interfaces/catalogs";
import { UUID } from "crypto";
import * as Sentry from "@sentry/react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/es";

type AddressBlockProps = {
  prefix: "particular" | "operativo";
  title: string;
  formik: ReturnType<typeof useFormik<PersonFormValues>>;
  disabled: boolean;
  required?: boolean;
  onCpLookup: (cp: string, prefix: "particular" | "operativo", coloniaId?: number) => void;
  colonias: { id: number; name: string }[];
};

const AddressBlock: React.FC<AddressBlockProps> = ({
  prefix,
  title,
  formik,
  disabled,
  required = false,
  onCpLookup,
  colonias,
}) => {
  const values = formik.values[prefix];
  const touched = formik.touched[prefix] || {};
  const errors = (formik.errors[prefix] || {}) as Partial<Record<keyof AddressFormValues, string>>;

  const setAddressField = (field: keyof AddressFormValues, value: string) => {
    formik.setFieldValue(`${prefix}.${field}`, value);
  };

  const fieldProps = (field: keyof AddressFormValues) => ({
    error: Boolean(touched[field] && errors[field]),
    helperText: touched[field] && errors[field] ? errors[field] : "",
  });

  return (
    <Box mt={4}>
      <Typography variant="body1" color="primary.dark" textAlign="center" fontWeight={700}>
        {title}
      </Typography>

      <TextField
        fullWidth
        margin="normal"
        label="Código Postal"
        required={required}
        value={values.cp}
        disabled={disabled}
        inputProps={{ maxLength: 5 }}
        onChange={(e) => {
          const cp = e.target.value.replace(/[^0-9]/g, "");
          setAddressField("cp", cp);
          if (cp.length === 5) onCpLookup(cp, prefix);
        }}
        onBlur={() => formik.setFieldTouched(`${prefix}.cp`, true)}
        {...fieldProps("cp")}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Calle o avenida"
        required={required}
        value={values.street}
        disabled={disabled}
        inputProps={{ maxLength: 100 }}
        onChange={(e) => setAddressField("street", e.target.value)}
        onBlur={() => formik.setFieldTouched(`${prefix}.street`, true)}
        {...fieldProps("street")}
      />

      <Grid container spacing={1}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            margin="normal"
            label="No. Exterior"
            required={required}
            value={values.no_ext}
            disabled={disabled}
            onChange={(e) => setAddressField("no_ext", e.target.value)}
            onBlur={() => formik.setFieldTouched(`${prefix}.no_ext`, true)}
            {...fieldProps("no_ext")}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            margin="normal"
            label="No. Interior"
            value={values.no_int}
            disabled={disabled}
            onChange={(e) => setAddressField("no_int", e.target.value)}
            onBlur={() => formik.setFieldTouched(`${prefix}.no_int`, true)}
          />
        </Grid>
      </Grid>

      <FormControl fullWidth margin="normal" error={Boolean(touched.colonia_id && errors.colonia_id)}>
        <InputLabel id={`${prefix}-colonia`}>Colonia</InputLabel>
        <Select
          labelId={`${prefix}-colonia`}
          label="Colonia"
          value={values.colonia_id}
          disabled={disabled}
          onChange={(e) => setAddressField("colonia_id", String(e.target.value))}
          onBlur={() => formik.setFieldTouched(`${prefix}.colonia_id`, true)}
        >
          {colonias.map((colonia) => (
            <MenuItem key={colonia.id} value={String(colonia.id)}>
              {colonia.name}
            </MenuItem>
          ))}
          <MenuItem value={OTHER_COLONIA_ID}>Otra</MenuItem>
        </Select>
        {touched.colonia_id && errors.colonia_id && <FormHelperText>{errors.colonia_id}</FormHelperText>}
      </FormControl>

      {values.colonia_id === OTHER_COLONIA_ID && (
        <TextField
          fullWidth
          margin="normal"
          label="Otra colonia"
          required={required}
          value={values.other_neighborhood}
          disabled={disabled}
          onChange={(e) => setAddressField("other_neighborhood", e.target.value)}
          onBlur={() => formik.setFieldTouched(`${prefix}.other_neighborhood`, true)}
          {...fieldProps("other_neighborhood")}
        />
      )}

      <TextField fullWidth margin="normal" label="Ciudad" value={values.city} disabled />
      <TextField fullWidth margin="normal" label="Municipio" value={values.locality} disabled />
      <TextField fullWidth margin="normal" label="Entidad" value={values.state} disabled />
    </Box>
  );
};

type DynamicPersonFormProps = {
  person: PersonaStructure;
  formsConfig: PersonFormsConfig;
  allPersons: PersonaStructure[];
  onSaved: (updated?: PersonaStructure) => void;
  onBack: () => void;
  hideTitle?: boolean;
};

const DynamicPersonForm: React.FC<DynamicPersonFormProps> = ({
  person,
  formsConfig,
  allPersons,
  onSaved,
  onBack,
  hideTitle = false,
}) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const personTypeCodes = useMemo(
    () => (person.person_types_detail || []).map((item) => item.code),
    [person.person_types_detail],
  );

  const principalTypeCodes = useMemo(() => {
    const principal = allPersons.find((candidate) => candidate.spouse_id === person.id);
    if (!principal) return [];
    return (principal.person_types_detail || []).map((item) => item.code);
  }, [allPersons, person.id]);

  const fields = useMemo(
    () => resolveFieldsForPerson(personTypeCodes, formsConfig, principalTypeCodes),
    [personTypeCodes, formsConfig, principalTypeCodes],
  );

  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const marriedIdRef = useRef("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [marriedId, setMarriedId] = useState("");
  const [civilStatuses, setCivilStatuses] = useState<CivilStatus[]>([]);
  const [regimes, setRegimes] = useState<MatrimonialRegimesData[]>([]);
  const [states, setStates] = useState<StateStructure[]>([]);
  const [spouseOptions, setSpouseOptions] = useState<PersonaStructure[]>([]);
  const [particularColonias, setParticularColonias] = useState<{ id: number; name: string }[]>([]);
  const [operativoColonias, setOperativoColonias] = useState<{ id: number; name: string }[]>([]);
  const [particularAddress, setParticularAddress] = useState<AddressData | undefined>();
  const [operativoAddress, setOperativoAddress] = useState<AddressData | undefined>();
  const [particularType, setParticularType] = useState<Address | undefined>();
  const [operativoType, setOperativoType] = useState<Address | undefined>();

  const [getByCp] = useLazyGetByCpQuery();
  const [getAddress] = useLazyGetAddressQuery();
  const [getAddressType] = useLazyGetAddressTypeQuery();
  const [getCivilStatus] = useLazyGetCivilStatusQuery();
  const [getMatrimonialRegimes] = useLazyGetMatrimonialRegimesQuery();
  const [getPersonType] = useLazyGetPersonTypeQuery();
  const [getStates] = useLazyGetStatesQuery();
  const [patchPerson] = usePatchPersonMutation();
  const [postPerson] = usePostPersonMutation();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();

  const formik = useFormik<PersonFormValues>({
    initialValues: buildInitialValues(),
    enableReinitialize: false,
    validateOnMount: true,
    validateOnBlur: false,
    validationSchema: yup.lazy(() => buildValidationSchema(fieldsRef.current, marriedIdRef.current)),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setIsSaving(true);

        let spouseId = values.spouse_id || null;
        if (fields.spouse_id?.enabled && values.civil_status_id === marriedId && values.spouse_id === OTHER_PERSON_ID) {
          const personTypes = await getPersonType().unwrap();
          const conyugeType = personTypes.find((item) => item.code === PERSON_CATALOG.conyuge_aval.code);
          if (!conyugeType) {
            throw new Error("No se encontró el tipo de persona cónyuge");
          }

          const created = await postPerson({
            body: {
              name: values.spouse_name,
              last_name: values.spouse_last_name,
              last_name_2: values.spouse_last_name_2 || undefined,
              loan_application_id: application.id,
              client_details_id: clientDetails.id,
              person_types: [conyugeType.id],
            },
          }).unwrap();
          spouseId = created.data.id;
        }

        const payload = buildPersonPatchPayload({
          values,
          fields,
          isMarried: values.civil_status_id === marriedId,
          spouseId,
        });

        const patched = await patchPerson({ personId: person.id, body: payload }).unwrap();

        if (fields.domicilio_particular?.enabled) {
          await saveAddress(values.particular, particularType, particularAddress);
        }
        if (fields.domicilio_operativo?.enabled) {
          await saveAddress(values.operativo, operativoType, operativoAddress);
        }

        onSaved({
          ...person,
          ...payload,
          ...(patched.data || {}),
          spouse_id: spouseId || person.spouse_id,
          personal_information_status: "COMPLETO",
        } as PersonaStructure);
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError.status === 409) {
          formik.setFieldError("email", "El correo electrónico ya se encuentra registrado");
          return;
        }
        Sentry.captureException(error, { extra: { component: "DynamicPersonForm-submit" } });
        dispatch(setModalErrorVisible({ open: true, error: apiError }));
      } finally {
        setIsSaving(false);
        setSubmitting(false);
      }
    },
  });

  const saveAddress = async (
    values: AddressFormValues,
    addressType: Address | undefined,
    existing: AddressData | undefined,
  ) => {
    if (!addressType?.id || !clientDetails?.id) return;

    const payload = buildAddressPayload({
      values,
      clientDetailsId: clientDetails.id,
      addressTypeId: addressType.id,
      personId: person.id,
    });

    if (existing?.id) {
      await patchAddress({ addressId: existing.id as UUID, data: payload }).unwrap();
    } else {
      await postAddress(payload).unwrap();
    }
  };

  const fetchLocationData = async (cp: string, prefix: "particular" | "operativo", coloniaId?: number) => {
    try {
      if (cp.length !== 5) return;

      const value = await getByCp(cp).unwrap();
      const { colonias, cities, states, localities } = value;

      formik.setFieldValue(`${prefix}.state_id`, String(states[0]?.id || ""));
      formik.setFieldValue(`${prefix}.state`, states[0]?.name || "");
      formik.setFieldValue(`${prefix}.city_id`, String(cities[0]?.id || ""));
      formik.setFieldValue(`${prefix}.city`, cities[0]?.name || "");
      formik.setFieldValue(`${prefix}.locality_id`, String(localities[0]?.id || ""));
      formik.setFieldValue(`${prefix}.locality`, localities[0]?.name || "");

      const list = (colonias || []).map((item: { id: number; name: string }) => ({
        id: item.id,
        name: item.name,
      }));
      if (prefix === "particular") setParticularColonias(list);
      else setOperativoColonias(list);

      if (coloniaId && coloniaId !== -1) {
        const selected = list.find((coloniaItem: { id: number; name: string }) => coloniaItem.id === coloniaId);
        formik.setFieldValue(`${prefix}.colonia_id`, selected ? String(selected.id) : "");
      } else if (coloniaId === -1) {
        formik.setFieldValue(`${prefix}.colonia_id`, OTHER_COLONIA_ID);
      } else {
        formik.setFieldValue(`${prefix}.colonia_id`, list[0] ? String(list[0].id) : "");
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "DynamicPersonForm-fetchLocationData", prefix },
      });
    }
  };

  const loadInitialData = async () => {
    try {
      setIsLoading(true);

      const [civilRes, regimesRes, addressTypes] = await Promise.all([
        fields.civil_status_id?.enabled || fields.matrimonial_regimes_id?.enabled || fields.spouse_id?.enabled
          ? getCivilStatus().unwrap()
          : Promise.resolve([]),
        fields.matrimonial_regimes_id?.enabled ? getMatrimonialRegimes().unwrap() : Promise.resolve([]),
        fields.domicilio_particular?.enabled || fields.domicilio_operativo?.enabled
          ? getAddressType().unwrap()
          : Promise.resolve([]),
      ]);

      setCivilStatuses(civilRes || []);
      setRegimes(regimesRes || []);
      const married = (civilRes || []).find((item) => item.name.toLowerCase() === "casado");
      setMarriedId(married?.id || "");
      marriedIdRef.current = married?.id || "";

      if (fields.birth_state?.enabled) {
        const statesRes = await getStates().unwrap();
        setStates(statesRes || []);
      }

      if (fields.spouse_id?.enabled) {
        setSpouseOptions([
          ...allPersons.filter((item) => item.id !== person.id),
          {
            id: OTHER_PERSON_ID,
            name: "Otro",
            last_name: "",
            last_name_2: "",
          } as PersonaStructure,
        ]);
      }

      const particularTypeItem = (addressTypes || []).find((item) => item.code === ADDRESS_CODES.particular.code);
      const operativoTypeItem = (addressTypes || []).find((item) => item.code === ADDRESS_CODES.operativo.code);
      setParticularType(particularTypeItem);
      setOperativoType(operativoTypeItem);

      let particularAddr: AddressData | undefined;
      let operativoAddr: AddressData | undefined;

      if (fields.domicilio_particular?.enabled || fields.domicilio_operativo?.enabled) {
        const addresses = await getAddress({ personId: person.id }).unwrap();
        particularAddr = addresses.find((item) => item.address_type_id === particularTypeItem?.id);
        operativoAddr = addresses.find((item) => item.address_type_id === operativoTypeItem?.id);
        setParticularAddress(particularAddr);
        setOperativoAddress(operativoAddr);
      }

      formik.setValues(buildInitialValues(person, particularAddr, operativoAddr));

      if (particularAddr?.cp) {
        await fetchLocationData(
          particularAddr.cp,
          "particular",
          particularAddr.other_neighborhood ? -1 : particularAddr.colonia_id,
        );
      }
      if (operativoAddr?.cp) {
        await fetchLocationData(
          operativoAddr.cp,
          "operativo",
          operativoAddr.other_neighborhood ? -1 : operativoAddr.colonia_id,
        );
      }
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, { extra: { component: "DynamicPersonForm-load" } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [person.id]);

  const showMarriedFields = formik.values.civil_status_id === marriedId && Boolean(marriedId);
  const disabled = isLoading || isSaving || formik.isSubmitting;

  if (!hasEnabledPersonFields(fields)) {
    return (
      <Box mt={3} p={2} border="1px solid" borderColor="divider" borderRadius={2}>
        <Typography color="primary.dark" textAlign="center">
          No hay campos configurados para los tipos de esta persona
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box mt={3} p={2.5} textAlign="left">
        {!hideTitle && (
          <Typography variant="h6" color="primary.dark" fontWeight={700} mb={2} textAlign="center">
            Información personal y domicilio
          </Typography>
        )}

        <form onSubmit={formik.handleSubmit} noValidate>
          <TextField fullWidth margin="normal" label="Nombre completo" value={getPersonFullName(person)} disabled />

          {fields.email?.enabled && (
            <TextField
              fullWidth
              margin="normal"
              label="Correo electrónico"
              name="email"
              required={fields.email.required}
              value={formik.values.email}
              disabled={disabled}
              inputProps={{ maxLength: 100 }}
              onChange={(e) => formik.setFieldValue("email", (e.target.value || "").trim().toLowerCase())}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          )}

          {fields.cell_phone_number?.enabled && (
            <Grid container spacing={1} mt={0.5}>
              <Grid item xs={4}>
                <CustomCountryCode
                  label="Código"
                  name="country_code"
                  value={formik.values.country_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Teléfono celular"
                  name="phone_number"
                  required={fields.cell_phone_number.required}
                  value={formik.values.phone_number}
                  disabled={disabled}
                  inputProps={{ maxLength: 10, inputMode: "numeric" }}
                  onChange={(e) => formik.setFieldValue("phone_number", e.target.value.replace(/[^0-9]/g, ""))}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                  helperText={formik.touched.phone_number && formik.errors.phone_number}
                />
              </Grid>
            </Grid>
          )}

          <Grid container spacing={1}>
            {fields.rfc?.enabled && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="RFC"
                  name="rfc"
                  required={fields.rfc.required}
                  value={formik.values.rfc}
                  disabled={disabled}
                  inputProps={{ maxLength: 13 }}
                  onChange={(e) => formik.setFieldValue("rfc", e.target.value.toUpperCase())}
                  onBlur={formik.handleBlur}
                  error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                  helperText={formik.touched.rfc && formik.errors.rfc}
                />
              </Grid>
            )}
            {fields.curp?.enabled && (
              <Grid item xs={12} sm={fields.rfc?.enabled ? 6 : 12}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="CURP"
                  name="curp"
                  required={fields.curp.required}
                  value={formik.values.curp}
                  disabled={disabled}
                  inputProps={{ maxLength: 18 }}
                  onChange={(e) => formik.setFieldValue("curp", e.target.value.toUpperCase())}
                  onBlur={formik.handleBlur}
                  error={formik.touched.curp && Boolean(formik.errors.curp)}
                  helperText={formik.touched.curp && formik.errors.curp}
                />
              </Grid>
            )}
          </Grid>

          {fields.occupation?.enabled && (
            <TextField
              fullWidth
              margin="normal"
              label="Ocupación"
              name="occupation"
              required={fields.occupation.required}
              value={formik.values.occupation}
              disabled={disabled}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.occupation && Boolean(formik.errors.occupation)}
              helperText={formik.touched.occupation && formik.errors.occupation}
            />
          )}

          {fields.birth_date?.enabled && (
            <FormControl fullWidth margin="normal">
              <DatePicker
                label="Fecha de nacimiento"
                value={formik.values.birth_date ? dayjs(formik.values.birth_date) : null}
                disabled={disabled}
                onChange={(value: Dayjs | null) => {
                  formik.setFieldValue("birth_date", value ? value.format("YYYY-MM-DD") : "");
                }}
                slotProps={{
                  textField: {
                    required: fields.birth_date.required,
                    error: formik.touched.birth_date && Boolean(formik.errors.birth_date),
                    helperText: formik.touched.birth_date && formik.errors.birth_date,
                    onBlur: formik.handleBlur,
                    name: "birth_date",
                  },
                }}
              />
            </FormControl>
          )}

          {fields.birth_state?.enabled && (
            <FormControl
              fullWidth
              margin="normal"
              error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
            >
              <InputLabel id="birth_state">Lugar de nacimiento</InputLabel>
              <Select
                labelId="birth_state"
                label="Lugar de nacimiento"
                name="birth_state"
                value={formik.values.birth_state}
                disabled={disabled}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {states.map((state) => (
                  <MenuItem key={state.id} value={String(state.id)}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.birth_state && formik.errors.birth_state && (
                <FormHelperText>{formik.errors.birth_state}</FormHelperText>
              )}
            </FormControl>
          )}

          {fields.sex?.enabled && (
            <FormControl fullWidth margin="normal" error={formik.touched.sex && Boolean(formik.errors.sex)}>
              <InputLabel id="sex">Sexo</InputLabel>
              <Select
                labelId="sex"
                label="Sexo"
                name="sex"
                value={formik.values.sex}
                disabled={disabled}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="MASCULINO">Masculino</MenuItem>
                <MenuItem value="FEMENINO">Femenino</MenuItem>
              </Select>
              {formik.touched.sex && formik.errors.sex && <FormHelperText>{formik.errors.sex}</FormHelperText>}
            </FormControl>
          )}

          {fields.civil_status_id?.enabled && (
            <FormControl
              fullWidth
              margin="normal"
              error={formik.touched.civil_status_id && Boolean(formik.errors.civil_status_id)}
            >
              <InputLabel id="civil_status_id">Estado civil</InputLabel>
              <Select
                labelId="civil_status_id"
                label="Estado civil"
                name="civil_status_id"
                value={formik.values.civil_status_id}
                disabled={disabled}
                onChange={(e) => {
                  formik.handleChange(e);
                  if (e.target.value !== marriedId) {
                    formik.setFieldValue("matrimonial_regimes_id", "");
                    formik.setFieldValue("spouse_id", "");
                  }
                }}
                onBlur={formik.handleBlur}
              >
                {civilStatuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.civil_status_id && formik.errors.civil_status_id && (
                <FormHelperText>{formik.errors.civil_status_id}</FormHelperText>
              )}
            </FormControl>
          )}

          {fields.matrimonial_regimes_id?.enabled && showMarriedFields && (
            <FormControl
              fullWidth
              margin="normal"
              error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
            >
              <InputLabel id="matrimonial_regimes_id">Régimen matrimonial</InputLabel>
              <Select
                labelId="matrimonial_regimes_id"
                label="Régimen matrimonial"
                name="matrimonial_regimes_id"
                value={formik.values.matrimonial_regimes_id}
                disabled={disabled}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {regimes.map((regime) => (
                  <MenuItem key={regime.id} value={regime.id}>
                    {regime.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.matrimonial_regimes_id && formik.errors.matrimonial_regimes_id && (
                <FormHelperText>{formik.errors.matrimonial_regimes_id}</FormHelperText>
              )}
            </FormControl>
          )}

          {fields.spouse_id?.enabled && showMarriedFields && (
            <>
              <FormControl
                fullWidth
                margin="normal"
                error={formik.touched.spouse_id && Boolean(formik.errors.spouse_id)}
              >
                <InputLabel id="spouse_id">Cónyuge</InputLabel>
                <Select
                  labelId="spouse_id"
                  label="Cónyuge"
                  name="spouse_id"
                  value={formik.values.spouse_id}
                  disabled={disabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {spouseOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.id === OTHER_PERSON_ID ? "Otro" : getPersonFullName(option)}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.spouse_id && formik.errors.spouse_id && (
                  <FormHelperText>{formik.errors.spouse_id}</FormHelperText>
                )}
              </FormControl>

              {formik.values.spouse_id === OTHER_PERSON_ID && (
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Nombre"
                      name="spouse_name"
                      required
                      value={formik.values.spouse_name}
                      disabled={disabled}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.spouse_name && Boolean(formik.errors.spouse_name)}
                      helperText={formik.touched.spouse_name && formik.errors.spouse_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Apellido paterno"
                      name="spouse_last_name"
                      required
                      value={formik.values.spouse_last_name}
                      disabled={disabled}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.spouse_last_name && Boolean(formik.errors.spouse_last_name)}
                      helperText={formik.touched.spouse_last_name && formik.errors.spouse_last_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Apellido materno"
                      name="spouse_last_name_2"
                      value={formik.values.spouse_last_name_2}
                      disabled={disabled}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {fields.domicilio_particular?.enabled && (
            <AddressBlock
              prefix="particular"
              title="Domicilio particular"
              formik={formik}
              disabled={disabled}
              required={Boolean(fields.domicilio_particular.required)}
              colonias={particularColonias}
              onCpLookup={fetchLocationData}
            />
          )}

          {fields.domicilio_operativo?.enabled && (
            <AddressBlock
              prefix="operativo"
              title="Domicilio operativo"
              formik={formik}
              disabled={disabled}
              required={Boolean(fields.domicilio_operativo.required)}
              colonias={operativoColonias}
              onCpLookup={fetchLocationData}
            />
          )}

          <Box
            display="flex"
            flexDirection={isMobileOrTablet ? "column" : "row"}
            justifyContent="center"
            gap={2}
            my={4}
          >
            <Button
              fullWidth={isMobileOrTablet}
              variant="blue_outlined"
              sx={{
                padding: isMobileOrTablet ? "0" : "0 24px",
                minWidth: isMobileOrTablet ? undefined : 180,
              }}
              onClick={onBack}
              disabled={formik.isSubmitting || isSaving}
            >
              Regresar al inicio
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth={isMobileOrTablet}
              color="primary"
              sx={{
                minWidth: isMobileOrTablet ? undefined : 180,
              }}
              disabled={!formik.isValid || formik.isSubmitting || isSaving || isLoading}
            >
              Continuar
            </Button>
          </Box>
        </form>
      </Box>
    </LocalizationProvider>
  );
};

export default DynamicPersonForm;
